import { useAlertsStore } from '@/store/pinia/alerts'
import { useAuthStore } from '@/store/pinia/auth'
import { useRootStore } from '@/store/pinia/root'
import { getOwnerUsername, hasNoOwner } from '@/utils/helpers'
import { InternalError, AppError, types as errTypes } from '../config/errors'
import { pinia } from '../plugins/pinia'

/**
 * Send Request(s) and handle responses according Cycloid conventions and using
 * the resources used by Cycloid.io Web App
 *
 * send operates as follow:
 * Send a HTTP requests and always resolve the promise despite of an error,
 * because the error is handled commonly across the application, the caller
 * must not do anything with the error.
 * However the caller may want to execute some specific logic (e.g. clear the
 * user credentials) if the request hasn't been fulfilled, so the resolved
 * promise contains a Response object or null when there has been an error.
 *
 * @typedef {function} send
 * @param {Request} req - The request to send
 * @param {number[]} acceptedStatuses - The list of HTTP response status codes
 *            which the caller wants to handle; All 2XX codes are considered
 *            successful so there is not need to add those to this list.
 * @returns {Promise} - It always resolve the promise if the `fetch` function
 *            resolves the promise, that means that the promise is always resolved
 *            despite of the server response, but it's resolved in the following
 *            manner:
 *            - If the response is OK, it returns the original Response object
 *            - If the response is no OK (no 2XX and nor present in the
 *              acceptedStatuses) then return null; then this happen the client
 *              should do anything with the error because it's handled by the
 *              application mechanism using our internal Errors and the store,
 *              however the caller will easily know that cannot do anything
 *              with the response because it's NULL and do some generic thing
 *              on top of the global error handling application logic, but with
 *              no information of the error at all, for example showing a temporary
 *              bubble about the error if the general one isn't enough.
 */

/**
 * InternalHelpers is a class to pin the implementations of the native interfaces
 * used by the internal functions and keep each HTTP client instance isolated
 * after its creation.
 * @class
 *
 * The object of this class doesn't have any state rather than mentioned interfaces
 * implementations.
 */
class InternalHelpers {
  /**
   * Create and object with 2 methods for creating a Request instance for a
   * public and authorized API requests.
   *
   * @param {Object} baseInit - Base Request init object for all the requests
   * @param {string} baseURL - the API base URL
   * @returns {Object} - Return an object with the 2 mentioned methods
   * @throws SyntaxError if the body cannot be JSON stringified
   */
  createRequestCtors (baseInit, baseURL) {
    // Content-Header cannot be set in headers with the guard set to "request"
    // which is obviously set to the headers obtained from a Request object,
    // hence we don't need to append such header because it's discarded.
    // @see [Using Fetch Guard]{@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#Guard}
    function request (init, path, { body, query }) {
      const headers = new Headers()
      let jsonBody

      if (body) {
        jsonBody = JSON.stringify(body)
      }
      return new Request(
        self.composeURL(baseURL, path, query),
        self.extendsInit(init, { body: jsonBody, headers }),
      )
    }

    const self = this
    return {
      createPublicReq (method, path, bodyAndQuery) {
        if (!bodyAndQuery) {
          bodyAndQuery = {}
        }

        const init = self.extendsInit(baseInit, { method })
        return request(init, path, bodyAndQuery)
      },
      createAuthReq (method, path, bodyAndQuery, token) {
        if (!bodyAndQuery) {
          bodyAndQuery = {}
        }

        const authStore = useAuthStore()

        const init = self.extendsInit(baseInit, {
          method,
          headers: {
            Authorization: `Bearer ${token || authStore.jwt}`,
          },
        })
        return request(init, path, bodyAndQuery)
      },
    }
  }

  /**
   * Create an URL formed by the base URL, path and query parameters
   *
   * @param {string} baseURL - The baseURL without ending with '/'
   * @param {string} path - The URL path to request starting with '/' or without
   * @param {Object} [query] - An object which represents the query parameters to
   *              request
   * @returns {string} - The full URL composed by the 3 provided parameters
   */
  composeURL (baseURL, path, query = null) {
    if (path[0] !== '/') {
      path = `/${path}`
    }

    let params = ''
    if (query) {
      params = `?${(new URLSearchParams(query)).toString()}`
    }

    return `${baseURL}${path}${params}`
  }

  /**
   * Create a new init object from the base and the additions. The properties of
   * additions overrides the base ones except the headers, which are merged.
   *
   * @see [init object Request parameter] {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request#Parameters}
   * @param {Object} base - Base Request init object
   * @param {Object} additions - Additions Request init object
   * @returns {Object} - New Request init object
   */
  extendsInit (base, additions) {
    const headers = new Headers()
    const dst = Object.assign({}, base, additions)

    // Merge headers
    for (const sh of [base.headers, additions.headers]) {
      if (!sh) {
        continue
      }

      const entries = (sh.entries) ? sh.entries() : Object.entries(sh)
      for (const [k, v] of entries) {
        headers.append(k, v)
      }
    }

    dst.headers = headers
    return dst
  }

  /**
   * Constructs an `AppError` instance based on the given error and request context.
   *
   * - If the error is an `InternalError`, it generates a system-level error with translated messaging.
   * - Otherwise, it attempts to read the request body,
   *   and wraps the original error into an `AppError` with network-level context.
   *
   * @async
   * @param {Error} err - The original error caught during request handling.
   * @param {Request} req - The original `fetch` request object.
   * @param {Request} reqBackup - A clone of `req`, used to retrieve the request body if needed.
   * @param {string} [reqBackupBody] - Pre-fetched body of the request; if not provided, it will be read.
   * @param {Object} i18n - The `vue-i18n` instance for fetching localized error messages.
   * @returns {Promise<AppError>} - A promise resolving to an `AppError` instance enriched with HTTP metadata and localization.
   */
  async buildAppError (err, req, reqBackup, reqBackupBody, i18n) {
    if (err instanceof InternalError) {
      return AppError.fromInternalError(err, i18n.global.t('errors.system'))
    }

    const metadata = {
      httpRequest: {
        method: req.method,
        url: req.url,
      },
    }

    if (!reqBackupBody) {
      try {
        reqBackupBody = await reqBackup.text()
        metadata.httpRequest.body = reqBackupBody
      } catch (e) {
        metadata.httpRequest.reason_no_body = e
      }
    } else {
      metadata.httpRequest.body = reqBackupBody
    }

    return AppError.fromError(
      err,
      'youdeploy-api-http-client.send: FetchAPI promise rejection',
      i18n.global.t('errors.network'),
      errTypes.SYS,
      metadata,
    )
  }

  /**
   * Construct an InternalError instance representing an unexpected HTTP response.
   *
   * Determines whether the error type is an API or app error based on the HTTP
   * status code, and includes relevant metadata about the HTTP request and response.
   *
   * @param {Request} req - The original Request object.
   * @param {Response} res - The Response object received from the fetch call.
   * @param {string} reqBody - The request body text (used for error context).
   * @param {string} resBody - The response body text (used for error context).
   * @returns {InternalError} - An error object enriched with HTTP metadata.
   */
  buildInternalError (req, res, reqBody, resBody) {
    // If it's >=500 it's an API error, below that, it should be an
    // app error because it's not handling the status code for some
    // reason
    const type = res.status >= 500 ? errTypes.API : errTypes.APP
    return new InternalError(
      'youdeploy-api-http-client.send: Unexpected API response status code',
      type,
      {
        httpRequest: {
          method: req.method,
          url: req.url,
          body: reqBody,
        },
        httpResponse: {
          status: res.status,
          body: resBody,
        },
      },
    )
  }

  /**
   * Handle accepted error status codes by parsing and modifying the response body
   * @param {Response} res - The response object
   * @returns {Promise<Response|null>} - Modified response or null if parsing fails
   */
  async handleAcceptedErrorStatus (res) {
    try {
      const resBody = await res.clone().text()
      const parsedBody = JSON.parse(resBody)

      if (parsedBody.errors && Array.isArray(parsedBody.errors)) {
        this.injectStatusCodeIntoErrors(parsedBody.errors, res.status)
        this.addRequestIdToFirstError(parsedBody)
        return this.createModifiedResponse(parsedBody, res)
      }
    } catch (e) {
      console.error('Failed to parse response body for status code injection:', e)
    }
    return null
  }

  /**
   * Inject status code into errors that don't have one
   * @param {Array} errors - Array of error objects
   * @param {number} statusCode - HTTP status code to inject
   */
  injectStatusCodeIntoErrors (errors, statusCode) {
    errors.forEach((error) => {
      if (!error.status_code) {
        error.status_code = statusCode
      }
    })
  }

  /**
   * Add request ID to the first error if available
   * @param {Object} parsedBody - Parsed response body
   */
  addRequestIdToFirstError (parsedBody) {
    if (parsedBody.request_id && parsedBody.errors[0]) {
      parsedBody.errors[0].request_id = parsedBody.request_id
    }
  }

  /**
   * Create a new Response object with modified body
   * @param {Object} parsedBody - Modified response body
   * @param {Response} originalRes - Original response object
   * @returns {Response} - New response with modified body
   */
  createModifiedResponse (parsedBody, originalRes) {
    return new Response(JSON.stringify(parsedBody), {
      status: originalRes.status,
      statusText: originalRes.statusText,
      headers: originalRes.headers,
    })
  }

  /**
   * Parse error response body and enrich with metadata
   * @param {Error} err - The error object
   * @returns {Object|null} - Parsed error object or null if parsing fails
   */
  parseErrorResponseBody (err) {
    const resBody = _.get(err, 'metadata.httpResponse.body')
    if (!resBody) return null

    const statusCode = _.get(err, 'metadata.httpResponse.status')
    try {
      return { resError: JSON.parse(resBody), statusCode }
    } catch (err) {
      console.error('Error parsing response body in buildAppError:', err)
      return null
    }
  }

  /**
   * Enrich error with metadata from parsed response
   * @param {Object|Array} error - The error to enrich
   * @param {Object} parsedData - Parsed response data
   * @returns {Object|Array} - Enriched error
   */
  enrichErrorWithMetadata (error, parsedData) {
    const { resError, statusCode } = parsedData

    if (_.has(resError, 'errors')) error = resError.errors
    if (_.has(resError, 'request_id') && _.isArray(error)) {
      error[0].request_id = resError.request_id
    }
    if (statusCode && _.isArray(error)) {
      error[0].status_code = statusCode
    }

    return error
  }

  /**
   * Build a dedup key from an error so that bursts of identical failures
   * (e.g. a wave of network rejections all yielding `errors.network`) collapse
   * into a single alert via the alerts store TTL-based suppression.
   * @param {Object|Array} error
   * @returns {String}
   */
  buildErrorDedupKey (error) {
    if (_.isArray(error)) {
      const first = _.first(error) ?? {}
      return `api:${first.status_code ?? 'unknown'}:${first.code ?? ''}:${first.message ?? ''}`
    }
    return `app:${error?.type ?? ''}:${error?.userMessage ?? error?.message ?? ''}`
  }

  /**
   * Handle alert display for unrecoverable errors
   * @param {Object|Array} error - The error to display
   * @param {Object} alertsStore - The alerts store instance
   */
  async handleErrorAlert (error, alertsStore) {
    const aborted = await alertsStore.SHOW_ALERT({
      type: 'error',
      content: _.isArray(error) ? error : [error],
      dedupKey: this.buildErrorDedupKey(error),
    })
    if (!aborted) console.error(error)
  }

  /**
   * Create a function to send requests and handle the common logic for send and
   * API error responses.
   *
   * @param {Object} i18n - Instance of vue-i18n
   * @returns {send} - The send function which use the provided, by parameters,
   *              resource instances
   */
  sendCtor (i18n) {
    const self = this
    return async function send (req, acceptedStatuses = [], { silent = false } = {}) {
      // Clone the request in case that the response won't be accepted and the
      // request body must be read after calling fetch which read the body of
      // the request.
      const alertsStore = useAlertsStore(pinia)

      const reqBackup = req.clone()
      let reqBackupBody
      try {
        const res = await fetch(req)
        if (!res.ok && !acceptedStatuses.includes(res.status)) {
          // Getting the body with text() method is a safe way for not getting
          // JSON parsing errors if the body isn't JSON format and we don't need
          // a JS Object for metadata purpose
          let resBody
          [reqBackupBody, resBody] = await Promise.all([reqBackup.text(), res.text()])
          throw self.buildInternalError(req, res, reqBackupBody, resBody)
        }

        // Handle accepted status codes that are not ok (like 422 for validation errors)
        if (!res.ok && acceptedStatuses.includes(res.status)) {
          const modifiedResponse = await self.handleAcceptedErrorStatus(res)
          if (modifiedResponse) {
            return modifiedResponse
          }
        }

        return res
      } catch (err) {
        let error = await self.buildAppError(err, req, reqBackup, reqBackupBody, i18n)

        const parsedData = self.parseErrorResponseBody(err)
        if (parsedData) {
          error = self.enrichErrorWithMetadata(error, parsedData)
        }

        if (!silent) await self.handleErrorAlert(error, alertsStore)
        return null
      }
    }
  }
}

/**
 * HTTP client expose 2 methods to perform public requests and authorized
 * responses, using the baseURL and API version provided to its constructor
 * function
 *
 * @typedef {Object} httpClient
 * @method publicRequest - Sends a request to the path without adding the authorization
 *              header
 * @method authRequest - Sends a request to the path adding the authorization
 *              header using the token present in the pinia store
 *
 * Both methods have the following parameters
 * @param {string} method - The HTTP method to use
 * @param {string} path - The path to request; It's append to the base URL
 * @param {Object} [queryAndBody]: Contains the query and/or body
 * @param {Object} [queryAndBody.query]: Plain object which represents the query
 *              parameters to send in the request
 * @param {Object} [queryAndBody.body]: Plain object which represents the body
 *              to send in the request
 * @param {number[]} [acceptedStatuses]: The list of status codes to accept as
 *              valid in order to return the response to avoid handling it as
 *              an common error. All the 2XX codes are always successful, so
 *              you don't have to add them, all the >299 status codes are treated
 *              as errors, so if the API endpoint that you're requesting can return
 *              an error which must be handled by the caller (e.g. 422 in a form
 *              component) you must added in the list.
 * @returns {Promise} - @see [send type]{@link send} defined in this file because
 *              these methods call it underneath. Send doc contains the explanation
 *              of the promise returned here
 */

/**
 * Create an object which has 2 methods to send requests to YD API.
 * One method is for sending requests without authentication and another to
 * send requests with it.
 *
 * @param {Object} i18n - Instance of vue-i18n
 * @returns {httpClient} - a new httpClient instance.
 *              An httpClient instance has 2 methods:
 *              * publicRequest: Which makes a request without sending the Authorization header
 *              * authRequest: Which makes a request sending the Authorization header and the
 *                             credential values present in the store
 *
 *              Both methods have the same parameters and they are the following:
 *              @param {string} method - The HTTP verb (e.g. GET, POST, ...)
 *              @param {string} path - The URL path to append to the baseURL for the request.
 *              @param {Object} bodyAndQuery - Object with 2 properties
 *              @param {Object} bodyAndQuery.body - The body to send with the request, obviously sending a body with
 *                    and HTTP verb which doesn't accept body may return an error or will be discarded, depending of
 *                    underlying WebAPI~Fetch implementation provided.
 *              @param {Object|Array} bodyAndQuery.query -  The query parameters of the request to send.
 *                    When it's an object, the property names are the name of the query parameters and property values
 *                    the associated query parameter values.
 *                    When it's an array, it must be an array of pairs; a pair is an array of just 2 items. An array
 *                    could be always use, but we advice to only use for one specific case that an object cannot
 *                    represent which is when it's needed to send the same query parameter name with different values
 *                    (e.g. ?env=prod&env=dev, then you are force to use an array in this format
 *                    [['env', 'prod'], ['env', 'dev']]).
 *                    These logic is the same that applied by the draft of the
 *                    [WebAPI~URLSearchParams]{@link https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams}
 *                    and it should be respected by the implementation passed to this constructor function.
 *              @param {number[]} acceptedStatuses - The list HTTP response status codes (<200 and >299) which are not
 *                    considered and error so the response will be returned rather than an error.
 *                    This is useful, because some statuses codes may be needed to be handled by an special logic
 *                    meanwhile the other must be considered errors and handle them commonly.
 *             @return {Promise} - Resolve with the response object returned by WebAPI~Fetch implementation when there
 *                    is a successful response (>=200 and <299) (which is defined by the 'ok' property that the
 *                    [Response]{@link https://developer.mozilla.org/en-US/docs/Web/API/Response#Properties}
 *                    object that WebAPI~Fetch implementation should return) or its status code is in the
 *                    acceptedStatuses codes array, otherwise the promise is resolved with `null` and the error (with
 *                    proper metadata) is reported to the event hub errors channel.
 *                    NOTE that the promise is never rejected, however it doesn't mean the errors are dropped, they
 *                    are reported to the store for being handled by the common application error handling, as
 *                    commented above, use acceptedStatuses  array to handle specific exceptions.
 *                    @see [alerts]{@link store/modules/alerts.js} and @see [errors]{@link utils/errors.js} for more
 *                    and deeper information of the implementation details.
 */
export default function createHTTPClient (
  i18n,
  baseURL = import.meta.env.VUE_APP_API_URL,
  apiVersion = import.meta.env.VUE_APP_API_VERSION,
) {
  const baseInit = {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'manual',
    referrer: 'client',
    headers: {
      'Content-Type': `application/vnd.cycloid.io.v${apiVersion}+json`,
    },
  }

  const lastIdx = baseURL.length - 1
  if (baseURL[lastIdx] === '/') baseURL = baseURL.slice(0, lastIdx)

  const helpers = new InternalHelpers()
  const { createPublicReq, createAuthReq } = helpers.createRequestCtors(baseInit, baseURL)
  const send = helpers.sendCtor(i18n)

  return {
    async publicRequest (method, path, bodyAndQuery = {}, acceptedStatuses = [], options = {}) {
      return send(createPublicReq(method, path, bodyAndQuery), acceptedStatuses, options)
    },
    async authRequest (method, path, bodyAndQuery = {}, acceptedStatuses = [], options = {}) {
      return send(createAuthReq(method, path, bodyAndQuery, options.token), acceptedStatuses, options)
    },
  }
}

/**
 * The function receives an object in order to create a list of pairs with it.
 * The list of pairs presents a key and a stringified value when the value of the
 * property object isn't an array; when the value is an array, one pair for each
 * array item will be add to the returned list of pairs having the object
 * property name as key, so there will be pairs witch the same key.
 *
 * For clarification a pair is an array of 2 values, in this context, the first
 * element of the array is named 'key' and the second 'value'
 *
 * Example:
 *  obj = { id: 10, label: ['project', 'client'], owner: { name: 'Cycloid' } }
 *  result = [['id', '10'], ['label', 'project'], ['label', 'client'], ['owner', '[object Object]']]
 *
 *  As you can see in the example the plain object assigned to the 'owner' property
 *  becomes such value because the toString method of Object is used to obtain
 *  it's stringified format.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams URLSearchParams}
 *
 * @param {Object} obj - The object to convert to an array of pairs
 * @returns {Array} - The array of pairs.
 */
export function objectToListOfPairs (obj) {
  if (!obj || (typeof obj !== 'object')) {
    throw new Error('the parameter must be an object')
  }

  const list = []
  for (const [p, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      for (const i of v) {
        list.push([p, i.toString()])
      }
      continue
    }

    list.push([p, v.toString()])
  }

  return list
}

/** Converts the .owner property to a `String` (username) or `null` (no owner assigned)
 *
 * Necessary for sending PUT and POST requests for entities with `.owner`.
 *
 * @param {Object}              item
 * @param {Object}              [options={}]
 * @param {boolean?}            [options.isCreation=false]
 *
 * @returns {username|null}
 */
export async function convertOwnerToUsername (item, { isCreation = false } = {}) {
  const rootStore = useRootStore(pinia)

  const { username, orgMembers } = rootStore
  const desiredUsername = getOwnerUsername(item?.owner ?? (isCreation ? username : null))
  const owner = _.find(orgMembers, ['username', desiredUsername])?.username
    ?? (isCreation && desiredUsername ? desiredUsername : null)

  hasNoOwner(owner)
    ? Object.assign(item, { owner: null })
    : Object.assign(item, { owner })

  if (!_.isNull(item.owner) && !_.isString(item.owner)) {
    console.error('Owner being sent to api is neither null nor a username. Please fix it.', { owner: item.owner })
  }

  return item
}
